import * as React from "react";
import { CreateOrUpdateSoftwareRelease, SoftwareRelease, SoftwareReleaseService } from "@app/software-releases";
import { ColumnConfig, wafflesKeyColumnName } from "@app/shared/waffles";
import { Waffles } from "@app/shared/waffles/Waffles";
import { Link } from "react-router-dom";
import moment from "moment";
import { toast } from "react-toastify";
import CreateSoftwareReleasePanelContent from "./CreateSoftwareReleasePanelContent";
import { FlyoutLink } from "@app/shared/Flyouts";
import { FontAwesome } from "@app/shared/icons/qesIcons";
import { JSX } from "react";
import Breadcrumbs from "@app/shared/breadcrumbs/Breadcrumbs";
import { breadcrumbTemplates, BreadcrumbTemplateEnum } from "@app/shared/breadcrumbs/breadcrumbTemplates";
import LoadingSpinner from "@app/shared/LoadingSpinner";
import Page from "@app/shared/Page";
import PageHeader from "@app/shared/PageHeader";
import PageHeaderRight from "@app/shared/PageHeaderRight";
import PageInner from "@app/shared/PageInner";
import PageSection from "@app/shared/PageSection";
import { QESAlert } from "@app/shared/qes-standards/Alert";

interface SoftwareReleasesTableData {
  dateReleased: string;
  releasePath: string;
  releaseVersion: JSX.Element,
  [wafflesKeyColumnName]: string;
}

const columnConfig: ColumnConfig = {
  /* oxlint-disable sort-keys */
  releaseVersion: {
    displayText: "Release Version",
    testId: "release-version",
  },
  releasePath: {
    displayText: "Download URL",
    testId: "release-path",
  },
  dateReleased: {
    displayText: "Release Date",
    testId: "date-released",
  },
  /* oxlint-enable sort-keys */
};

const breadcrumbs = breadcrumbTemplates[BreadcrumbTemplateEnum.QasSoftwareReleases];

export const SoftwareReleasesList: React.FC<React.PropsWithChildren<unknown>> = () => {
  const [softwareReleases, setSoftwareReleases] = React.useState<SoftwareRelease[]>([]);
  const [softwareReleasesTableData, setSoftwareReleasesTableData] = React.useState<SoftwareReleasesTableData[]>([]);
  const [error, setError] = React.useState("");
  const [loading, setLoading] = React.useState(true);
  const [refreshSoftwareReleaseList, setRefreshSoftwareReleaseList] = React.useState(false);

  React.useEffect(() => {
    // fetch softwareReleases
    const softwareReleaseService = new SoftwareReleaseService();
    softwareReleaseService.getAll().then(fetchSoftwareReleasesResolve, fetchSoftwareReleasesReject);
  }, [refreshSoftwareReleaseList]);

  const fetchSoftwareReleasesResolve = (softwareReleases: SoftwareRelease[]) => {
    setSoftwareReleases(softwareReleases);
    setSoftwareReleasesTableData(buildTableData(softwareReleases));
    setLoading(false);
    setRefreshSoftwareReleaseList(false);
  }


  const fetchSoftwareReleasesReject = () => {
    setError(`There was a problem retrieving software releases.`);
    setLoading(false);
    setRefreshSoftwareReleaseList(false);
  }

  const buildTableData = (data: SoftwareRelease[]): SoftwareReleasesTableData[] => {

    return data.map<SoftwareReleasesTableData>((softwareRelease) => {
      /* oxlint-disable sort-keys */
      return {
        releaseVersion: <Link key={softwareRelease.softwareReleaseId} to={`/admin/software-releases/${softwareRelease.softwareReleaseId.toString()}`}>{`${softwareRelease.releaseYear}.${softwareRelease.releaseQuarter}`}</Link>,
        releasePath: softwareRelease.releasePath,
        dateReleased: !!softwareRelease.dateReleased ? moment.utc(softwareRelease.dateReleased).local().format("L") : "(not released)",
        [wafflesKeyColumnName]: softwareRelease.softwareReleaseId.toString(),
      };
      /* oxlint-enable sort-keys */
    });
  }

  const handleCreateNewSoftwareRelease = (releaseVersion: string) => {
    const csr: CreateOrUpdateSoftwareRelease = {
      dateReleased: null,
      productSuiteId: null,
      releasePath: null,
      releaseQuarter: parseInt(releaseVersion.split('.')[1]),
      releaseYear: parseInt(releaseVersion.split('.')[0]),
    }

    const softwareReleaseService = new SoftwareReleaseService();
    softwareReleaseService.create(csr).then(() => {
      toast(<span>Software Release successfully created!</span>,
        {
          containerId: "z-index-9999",
          type: "success",
        },
      );
      setRefreshSoftwareReleaseList(true);
    }, (e) => {
      toast(<span>We encountered a problem creating a new Software Release: {e}.</span>,
        {
          containerId: "z-index-9999",
          type: "error",
        },
      );
    });
  };


  return (
    <>
      <Page documentTitle="Software Releases - Admin">
        <Breadcrumbs items={breadcrumbs} />
        <PageInner>
          <PageHeader title={"QAS Software Releases"} iconName={FontAwesome.Laptop} disableHR={false}>
            <PageHeaderRight>
              <FlyoutLink
                classNames="qes-button-primary"
                content={{
                  body: <CreateSoftwareReleasePanelContent handleConfirm={handleCreateNewSoftwareRelease} />,
                  title: "Add New Software Release",
                }}
                size="20"
                testId="software-releases__create-flyout"
              >
                Add New Software Release
              </FlyoutLink>
            </PageHeaderRight>
          </PageHeader>
          <PageSection>
            {!!error && <QESAlert type="danger" persist={true}>{error}</QESAlert>}
            {loading && <LoadingSpinner />}
            {!loading && softwareReleases.length === 0 && <p>No Software Releases were found.</p>}
            {!loading && softwareReleases.length > 0 && (
              <div>
                <Waffles<SoftwareReleasesTableData>
                  columns={columnConfig}
                  data={softwareReleasesTableData}
                  enablePagination={false}
                  fullWidth={true}
                  passThroughSorting={true}
                  testId="software-releases-table"
                />
              </div>
            )}
          </PageSection>
        </PageInner>
      </Page >
    </>
  );
}